import React, { useState } from 'react';
import { 
  X, Sparkles, ShieldCheck, CheckCircle2, Cpu, Terminal, 
  Database, Zap, ArrowRight, Copy, Check, ExternalLink, 
  Layers, Lock, Server, RefreshCw, AlertCircle, CreditCard,
  Building, Globe, Mail, Info
} from 'lucide-react';
import { MermaidDiagram } from './MermaidDiagram';

interface DeepBrainOsShowcaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  language?: string;
  userEmail?: string;
  initialTab?: 'overview' | 'architecture' | 'skills' | 'pricing';
}

export const DeepBrainOsShowcaseModal: React.FC<DeepBrainOsShowcaseModalProps> = ({
  isOpen,
  onClose,
  language = 'uk',
  userEmail = '',
  initialTab = 'overview'
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'developer' | 'cloud' | 'enterprise'>('developer');
  const [email, setEmail] = useState(userEmail || '');
  const [paymentMethod, setPaymentMethod] = useState<'mono' | 'iban' | 'international'>('mono');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedCmd, setCopiedCmd] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'architecture' | 'skills' | 'pricing'>(initialTab);

  React.useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab, isOpen]);

  if (!isOpen) return null;

  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const sampleInstallCmd = `curl -fsSL https://deepresearchhub.online/install | bash -s -- --key=DBO-2026-PRO`;

  const handleCopyCmd = () => {
    navigator.clipboard.writeText(sampleInstallCmd);
    setCopiedCmd(true);
    setTimeout(() => setCopiedCmd(false), 2000);
  };

  const handleCopyField = (text: string, fieldKey: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2500);
  };

  const getPlanPriceUah = () => {
    if (selectedPlan === 'developer') return '3 150 грн ($79)';
    if (selectedPlan === 'cloud') return '3 550 грн ($89)';
    return '23 600 грн ($590)';
  };

  const getPlanPriceNumber = () => {
    if (selectedPlan === 'developer') return '3150.00';
    if (selectedPlan === 'cloud') return '3550.00';
    return '23600.00';
  };

  const getPlanTitle = () => {
    if (selectedPlan === 'developer') return 'Deep Brain OS (Developer Kit)';
    if (selectedPlan === 'cloud') return 'Deep Brain OS (Cloud Server)';
    return 'Deep Brain OS (Enterprise Team)';
  };

  const ibanDetails = {
    recipient: 'ФОП Аксьонов Сергій Миколайович',
    taxId: '2648220818',
    iban: 'UA073220010000026003380021905',
    bank: 'АТ «УНІВЕРСАЛ БАНК» (monobank)',
    mfo: '322001',
    amount: getPlanPriceNumber(),
    purpose: `Оплата за ПЗ ${getPlanTitle()}, ліцензіат: ${email.trim() || 'email замовника'}`
  };

  const allIbanText = `Отримувач: ${ibanDetails.recipient}
ЄДРПОУ/РНОКПП: ${ibanDetails.taxId}
Рахунок IBAN: ${ibanDetails.iban}
Банк: ${ibanDetails.bank} (МФО ${ibanDetails.mfo})
Сума до сплати: ${ibanDetails.amount} грн
Призначення платежу: ${ibanDetails.purpose}`;

  const handleCreateInvoice = async () => {
    if (!email.trim() || !email.includes('@')) {
      setError(isUk ? '⚠️ Будь ласка, вкажіть вашу електронну пошту вище перед натисканням оплати.' : '⚠️ Please enter your email address above before proceeding.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/deep-brain-os/create-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), plan: selectedPlan })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Помилка створення рахунку');
      }

      if (data.pageUrl) {
        window.location.href = data.pageUrl;
      } else {
        throw new Error('Платіжне посилання не отримано');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Сталася помилка при ініціалізації оплати');
    } finally {
      setIsLoading(false);
    }
  };

  const mermaidPipeline = `flowchart TD
    Task["📝 task.md (Формулювання завдання)"] --> Planner["🧠 PLANNER (Архітектурний план)"]
    Planner --> IdeatorA["💡 IDEATOR-A (Прямий розв'язок)"]
    Planner --> IdeatorB["💡 IDEATOR-B (Альтернативний підхід)"]
    IdeatorA --> Solver["⚡ SOLVER (Порівняння та генерація solution.py)"]
    IdeatorB --> Solver
    Solver --> TestRunner["🧪 TEST RUNNER (Детерміновані тести в субпроцесі)"]
    TestRunner -- "100% PASS" --> Done["✅ ГОТОВИЙ ВАЛІДОВАНИЙ КОД"]
    TestRunner -- "FAIL / ПОМИЛКА" --> Reviewer["🔍 REVIEWER (Аналіз логів без коду)"]
    Reviewer --> Repair["🛠️ REPAIR WORKER (Точкове виправлення)"]
    Repair --> Guard{"🛡️ ROLLBACK GUARD"}
    Guard -- "Тест пройдено" --> Done
    Guard -- "Погіршення" --> Rollback["⏪ Атомарний відкат до робочої копії"]
    Rollback --> Done
`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-950/80 backdrop-blur-md overflow-y-auto animate-fade-in">
      <div className="relative w-full max-w-4xl bg-slate-900 border border-violet-500/40 rounded-3xl shadow-2xl shadow-violet-950/50 text-white overflow-hidden my-auto max-h-[92vh] flex flex-col">
        
        {/* Glow ambient effects */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-violet-600/25 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none" />

        {/* Modal Header */}
        <div className="relative p-5 sm:p-6 border-b border-slate-800 flex items-start justify-between gap-4 bg-slate-950/60">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-violet-500/20 text-violet-300 border border-violet-500/40 flex items-center gap-1 font-mono">
                <Sparkles className="w-3 h-3 text-amber-300" />
                <span>FLAGSHIP AI RELEASE 2026</span>
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-mono">
                5-AGENT LEDGER
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-300 border border-blue-500/40 font-mono">
                ZERO CONTEXT ROT
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight flex items-center gap-2">
              <span>🧠 Deep Brain OS</span>
              <span className="text-xs sm:text-sm font-normal text-violet-300 font-mono">v1.0 Pro Harness</span>
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">
              {isUk 
                ? 'Автономна операційна обв\'язка для ШІ-агентів: скомпільована вічна пам\'ять, 5-агентний конвеєр перевірки коду та захист секретів.'
                : 'Autonomous operating harness for AI agents: persistent compounding memory, 5-agent code verification, and zero context rot.'}
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors cursor-pointer shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="relative px-5 pt-3 border-b border-slate-800 bg-slate-950/40 flex items-center gap-2 overflow-x-auto text-xs font-bold font-mono">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-3 border-b-2 transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'overview' 
                ? 'border-violet-400 text-violet-300 font-black' 
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            📋 {isUk ? 'Огляд продукту' : 'Product Overview'}
          </button>
          <button
            onClick={() => setActiveTab('architecture')}
            className={`py-2 px-3 border-b-2 transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'architecture' 
                ? 'border-violet-400 text-violet-300 font-black' 
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            ⚡ {isUk ? 'Архітектура 5 агентів' : '5-Agent Pipeline'}
          </button>
          <button
            onClick={() => setActiveTab('skills')}
            className={`py-2 px-3 border-b-2 transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'skills' 
                ? 'border-violet-400 text-violet-300 font-black' 
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            🧰 {isUk ? '20+ Навичок & Захист' : '20+ Skills & Safety'}
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`py-2 px-3 border-b-2 transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'pricing' 
                ? 'border-emerald-400 text-emerald-300 font-black' 
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            💳 {isUk ? 'Тарифи та Придбання' : 'Pricing & Purchase'}
          </button>
        </div>

        {/* Modal Body */}
        <div className="relative p-5 sm:p-6 overflow-y-auto space-y-6 flex-1 text-slate-200 text-sm">
          
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-violet-950/60 via-purple-950/40 to-slate-900 border border-violet-500/30 rounded-2xl p-5 space-y-3">
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-300" />
                  <span>{isUk ? 'Чому стандартні ШІ-агенти ламаються, а Deep Brain OS працює місяцями?' : 'Why Standard AI Agents Fail vs Deep Brain OS'}</span>
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans">
                  {isUk 
                    ? 'Більшість агентів (Claude, Cursor, Aider) при збільшенні кодової бази починають втрачати контекст (Context Rot), галюцинують та ламають сусідні модулі. Deep Brain OS вирішує це на системному рівні: скомпільована довгострокова пам\'ять (LLM Wiki) замість роздутих промптів, багатоагентна верифікація коду детермінованими тестами та атомарний відкат у разі регресії.'
                    : 'Standard agents suffer from context rot, hallucinations, and breaking changes. Deep Brain OS solves this through write-time compiled LLM Wiki memory, 5-agent multi-perspective code generation, and subprocess deterministic test validation with atomic rollback.'}
                </p>
              </div>

              {/* 4 Key Pillars Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-violet-400 font-bold text-xs uppercase font-mono">
                    <Database className="w-4 h-4 text-violet-400" />
                    <span>{isUk ? '1. Скомпільована пам\'ять' : '1. Compounding Memory'}</span>
                  </div>
                  <p className="text-xs text-slate-300">
                    {isUk 
                      ? 'Агент веде внутрішню структуровану базу WIKI, журнал змін CHANGELOG та беклог з RICE-скорингом. Ніякої втрати контексту.'
                      : 'Persistent write-time compiled WIKI, CHANGELOG, and RICE idea scoring. No context degradation.'}
                  </p>
                </div>

                <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-emerald-400 font-bold text-xs uppercase font-mono">
                    <Cpu className="w-4 h-4 text-emerald-400" />
                    <span>{isUk ? '2. 5-Агентний Solver' : '2. 5-Agent Solver'}</span>
                  </div>
                  <p className="text-xs text-slate-300">
                    {isUk 
                      ? 'Planner -> два паралельних Ideator-и -> Solver -> Тести в субпроцесі -> Reviewer & Repair Guard з авто-відкатом.'
                      : 'Planner -> 2 parallel Ideators -> Solver -> Subprocess Tests -> Reviewer with Atomic Rollback.'}
                  </p>
                </div>

                <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-blue-400 font-bold text-xs uppercase font-mono">
                    <Lock className="w-4 h-4 text-blue-400" />
                    <span>{isUk ? '3. Varlock Secret Shield' : '3. Varlock Secret Shield'}</span>
                  </div>
                  <p className="text-xs text-slate-300">
                    {isUk 
                      ? 'Автоматичне виявлення та блокування витоку API-ключів, токенів та паролів баз даних у відкриті git-коміти.'
                      : 'Prevents leakage of sensitive API keys, DB passwords, and tokens into open commits or logs.'}
                  </p>
                </div>

                <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-amber-400 font-bold text-xs uppercase font-mono">
                    <Terminal className="w-4 h-4 text-amber-400" />
                    <span>{isUk ? '4. 1-Клікове розгортання' : '4. 1-Click Deployment'}</span>
                  </div>
                  <p className="text-xs text-slate-300">
                    {isUk 
                      ? 'Встановлення на будь-який Linux/Mac сервер або ПК за 3 хвилини однією командою з автоматичним налаштуванням оточення.'
                      : 'Deploys on any Linux/Mac server in 3 minutes via single curl command with automatic dependencies setup.'}
                  </p>
                </div>
              </div>

              {/* Terminal command snippet */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 space-y-2 font-mono">
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span className="flex items-center gap-1.5 text-emerald-400">
                    <Terminal className="w-3.5 h-3.5" />
                    <span>{isUk ? 'Команда швидкого встановлення:' : 'Quick Install Command:'}</span>
                  </span>
                  <button
                    onClick={handleCopyCmd}
                    className="flex items-center gap-1 text-[11px] text-violet-300 hover:text-white transition-colors cursor-pointer"
                  >
                    {copiedCmd ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedCmd ? (isUk ? 'Скопійовано!' : 'Copied!') : (isUk ? 'Копіювати' : 'Copy')}</span>
                  </button>
                </div>
                <div className="p-3 bg-black/60 rounded-xl text-emerald-300 text-xs break-all border border-slate-800/80">
                  {sampleInstallCmd}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: ARCHITECTURE */}
          {activeTab === 'architecture' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-emerald-400" />
                  <span>{isUk ? 'Мультиагентний конвеєр перевірки коду (Memdoor / GVS5H Benchmark)' : 'Multi-Agent Code Verification Pipeline'}</span>
                </h3>
                <p className="text-xs text-slate-300 font-sans">
                  {isUk 
                    ? 'Кожне складне алгоритмічне чи інженерне завдання вирішується ізольованою командою з 5 спеціалізованих ролей із детермінованими тестами та захистом від погіршення коду:'
                    : 'Each algorithmic task is handled by an isolated team of 5 specialized agents with deterministic test execution and atomic rollback guard:'}
                </p>
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 overflow-x-auto">
                <MermaidDiagram chart={mermaidPipeline} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800">
                  <span className="font-bold text-violet-300">Planner & Dual Ideators:</span>
                  <p className="text-slate-400 mt-0.5">Планувальник розбиває задачу, а два незалежних агенти паралельно створюють дві різні алгоритмічні гіпотези у свіжому контексті.</p>
                </div>
                <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800">
                  <span className="font-bold text-emerald-300">Atomic Rollback Guard:</span>
                  <p className="text-slate-400 mt-0.5">Якщо правка Reviewer погіршує проходження тестів, система миттєво відкатує код до останньої 100% робочої версії.</p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: SKILLS & SAFETY */}
          {activeTab === 'skills' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Layers className="w-5 h-5 text-blue-400" />
                  <span>{isUk ? 'Каталог із 20+ перевірених інженерних навичок' : '20+ Production Engineering Skills'}</span>
                </h3>
                <p className="text-xs text-slate-300 font-sans">
                  {isUk 
                    ? 'У комплект входять нативні навички (skills) для автоматичного виконання завдань без необхідності ручного промптингу:'
                    : 'Native skills pre-installed and ready to use out-of-the-box:'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800 space-y-1">
                  <span className="font-bold text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> token-rescue
                  </span>
                  <p className="text-slate-400">Стиснення контексту та оптимізація запитів. Економить до 90% витрат на токени LLM.</p>
                </div>

                <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800 space-y-1">
                  <span className="font-bold text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> systematic-debugging
                  </span>
                  <p className="text-slate-400">4-фазний протокол усунення несправностей (Reproduce ➔ Hypothesize ➔ Isolate ➔ Fix &amp; Verify).</p>
                </div>

                <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800 space-y-1">
                  <span className="font-bold text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> sql-query-optimizer
                  </span>
                  <p className="text-slate-400">Аналіз планів EXPLAIN ANALYZE, індекси B-Tree, GIN, GiST та оптимізація запитів PostgreSQL/SQLite.</p>
                </div>

                <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800 space-y-1">
                  <span className="font-bold text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> owasp-api-hardening
                  </span>
                  <p className="text-slate-400">Аудит вразливостей REST API: захист від SSRF, SQL/NoSQL ін'єкцій, BOLA та Rate Limit обходу.</p>
                </div>

                <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800 space-y-1">
                  <span className="font-bold text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> subagent-task-isolation
                  </span>
                  <p className="text-slate-400">Ізоляція важких сканувань коду у фонові субпроцеси без засмічення головного контексту.</p>
                </div>

                <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800 space-y-1">
                  <span className="font-bold text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> bionic-cognitive-engine
                  </span>
                  <p className="text-slate-400">Біонічна архітектура подвійної пам'яті (Active Inference, CLS, консолідація досвіду).</p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: PRICING & PURCHASE */}
          {activeTab === 'pricing' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-300" />
                  <span>{isUk ? 'Оберіть тарифний план для замовлення' : 'Select Your License Tier'}</span>
                </h3>
                <p className="text-xs text-slate-300 font-sans">
                  {isUk 
                    ? 'Миттєва оплата через Plata by mono (банківські картки, Apple Pay, Google Pay). Ліцензійний ключ генерується автоматично.'
                    : 'Instant checkout via Monobank (cards, Apple Pay, Google Pay) with automatic license issuance.'}
                </p>
              </div>

              {/* 3 Pricing Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                
                {/* Tier 1: Developer */}
                <div 
                  onClick={() => setSelectedPlan('developer')}
                  className={`p-4 rounded-2xl border-2 transition-all cursor-pointer relative flex flex-col justify-between ${
                    selectedPlan === 'developer'
                      ? 'bg-violet-950/50 border-violet-400 shadow-lg shadow-violet-950/50 scale-[1.02]'
                      : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold font-mono text-violet-300 uppercase">🛠️ Developer Kit</span>
                      {selectedPlan === 'developer' && <CheckCircle2 className="w-4 h-4 text-violet-400" />}
                    </div>
                    <div className="text-xl font-black text-white">
                      3 150 грн <span className="text-xs text-slate-400 font-normal">($79)</span>
                    </div>
                    <p className="text-[11px] text-slate-300">Одноразовий платіж для розробників на власному ПК чи сервері.</p>
                    <ul className="text-[11px] text-slate-400 space-y-1 pt-2 border-t border-slate-800">
                      <li>✓ 1-кліковий інсталятор</li>
                      <li>✓ Вічна пам'ять LLM Wiki</li>
                      <li>✓ 5-агентний Solver</li>
                      <li>✓ 20+ інженерних навичок</li>
                      <li>✓ 3 місяці оновлень</li>
                    </ul>
                  </div>
                </div>

                {/* Tier 2: Cloud Managed */}
                <div 
                  onClick={() => setSelectedPlan('cloud')}
                  className={`p-4 rounded-2xl border-2 transition-all cursor-pointer relative flex flex-col justify-between ${
                    selectedPlan === 'cloud'
                      ? 'bg-emerald-950/50 border-emerald-400 shadow-lg shadow-emerald-950/50 scale-[1.02]'
                      : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold font-mono text-emerald-300 uppercase">🚀 Cloud Server</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-mono">ПОПУЛЯРНИЙ</span>
                    </div>
                    <div className="text-xl font-black text-white">
                      3 550 грн <span className="text-xs text-slate-400 font-normal">/ міс ($89)</span>
                    </div>
                    <p className="text-[11px] text-slate-300">Готовий виділений сервер під ключ із підключеним Telegram-ботом.</p>
                    <ul className="text-[11px] text-slate-400 space-y-1 pt-2 border-t border-slate-800">
                      <li>✓ Окремий VPS (8-12 GB RAM)</li>
                      <li>✓ Керування через Telegram-бота</li>
                      <li>✓ Щомісячні автооновлення</li>
                      <li>✓ Резервне копіювання 24/7</li>
                      <li>✓ Нуль налаштувань у консолі</li>
                    </ul>
                  </div>
                </div>

                {/* Tier 3: Enterprise */}
                <div 
                  onClick={() => setSelectedPlan('enterprise')}
                  className={`p-4 rounded-2xl border-2 transition-all cursor-pointer relative flex flex-col justify-between ${
                    selectedPlan === 'enterprise'
                      ? 'bg-amber-950/50 border-amber-400 shadow-lg shadow-amber-950/50 scale-[1.02]'
                      : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold font-mono text-amber-300 uppercase">🏢 Enterprise</span>
                      {selectedPlan === 'enterprise' && <CheckCircle2 className="w-4 h-4 text-amber-400" />}
                    </div>
                    <div className="text-xl font-black text-white">
                      23 600 грн <span className="text-xs text-slate-400 font-normal">($590)</span>
                    </div>
                    <p className="text-[11px] text-slate-300">Індивідуальне впровадження для IT-студій та компаній.</p>
                    <ul className="text-[11px] text-slate-400 space-y-1 pt-2 border-t border-slate-800">
                      <li>✓ Кастомні навички під ваш стек</li>
                      <li>✓ Інтеграція з Notion / Jira</li>
                      <li>✓ Пряма підтримка архітектора</li>
                      <li>✓ Мульти-користувацький доступ</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* Checkout Form & Payment Methods */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4">
                
                {/* Email Input */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-300 block font-mono">
                    {isUk ? 'Ваша електронна пошта (для отримання ліцензії та інструкцій):' : 'Your Email (to receive license & instructions):'}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(null);
                    }}
                    placeholder="name@company.com"
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-hidden focus:border-violet-500 text-sm font-mono"
                  />
                  {!email.trim() && (
                    <p className="text-[11px] text-amber-400 font-mono">
                      {isUk ? 'ℹ️ Вкажіть пошту перед оплатою, щоб отримати ліцензійний ключ' : 'ℹ️ Enter email before payment to receive your license key'}
                    </p>
                  )}
                </div>

                {/* Payment Method Selector */}
                <div className="space-y-2 pt-2 border-t border-slate-900">
                  <div className="text-xs font-mono font-bold text-slate-400">
                    {isUk ? 'Оберіть зручний спосіб оплати:' : 'Choose Payment Method:'}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('mono')}
                      className={`px-3 py-2 rounded-xl text-xs font-bold font-mono flex items-center gap-1.5 transition-all cursor-pointer ${
                        paymentMethod === 'mono'
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 shadow-sm'
                          : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-white'
                      }`}
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>{isUk ? '💳 Plata by mono (Картка / Apple Pay)' : '💳 Card / Apple Pay'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('iban')}
                      className={`px-3 py-2 rounded-xl text-xs font-bold font-mono flex items-center gap-1.5 transition-all cursor-pointer ${
                        paymentMethod === 'iban'
                          ? 'bg-violet-500/20 text-violet-300 border border-violet-500/50 shadow-sm'
                          : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-white'
                      }`}
                    >
                      <Building className="w-3.5 h-3.5" />
                      <span>{isUk ? '🏦 Офіційний IBAN (ФОП Аксьонов)' : '🏦 Direct IBAN (FOP)'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('international')}
                      className={`px-3 py-2 rounded-xl text-xs font-bold font-mono flex items-center gap-1.5 transition-all cursor-pointer ${
                        paymentMethod === 'international'
                          ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50 shadow-sm'
                          : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-white'
                      }`}
                    >
                      <Globe className="w-3.5 h-3.5" />
                      <span>{isUk ? '🌍 USD / EUR / Lemon Squeezy' : '🌍 International'}</span>
                    </button>
                  </div>
                </div>

                {/* Error Banner */}
                {error && (
                  <div className="p-3 bg-red-950/80 border border-red-500/50 rounded-xl text-red-200 text-xs space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                      <span className="font-semibold">{error}</span>
                    </div>

                    {(error.includes('активний термінал') || error.includes('terminal') || error.includes('403') || error.includes('FORBIDDEN') || error.includes('Monobank')) && (
                      <div className="pt-2 border-t border-red-900/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                        <span className="text-[11px] text-amber-300">
                          {isUk 
                            ? 'Термінал Plata by mono очікує підтвердження в банку. Скористайтеся прямою оплатою за офіційними реквізитами IBAN:' 
                            : 'Terminal pending bank verification. Pay directly via IBAN:'}
                        </span>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('iban')}
                          className="px-3 py-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-lg transition-colors cursor-pointer shrink-0"
                        >
                          {isUk ? '👉 Оплатити через IBAN' : '👉 Pay via IBAN'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* METHOD 1: PLATA BY MONO */}
                {paymentMethod === 'mono' && (
                  <div className="space-y-4 pt-2">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-slate-900/80 border border-slate-800 rounded-xl">
                      <div className="text-xs text-slate-400 font-mono">
                        <span>{isUk ? 'Сума до сплати:' : 'Total Amount:'} </span>
                        <strong className="text-emerald-400 text-base font-bold ml-1">
                          {getPlanPriceUah()}
                        </strong>
                      </div>

                      <button
                        onClick={handleCreateInvoice}
                        disabled={isLoading}
                        className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-500 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-sm rounded-xl shadow-lg shadow-emerald-900/30 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                      >
                        {isLoading ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>{isUk ? 'Формування рахунку...' : 'Creating invoice...'}</span>
                          </>
                        ) : (
                          <>
                            <ShieldCheck className="w-4 h-4 text-emerald-200" />
                            <span>{isUk ? '💳 Оплатити через Plata by mono' : '💳 Pay with Card / Apple Pay'}</span>
                          </>
                        )}
                      </button>
                    </div>

                    <div className="text-[11px] text-slate-400 text-center font-mono">
                      {isUk 
                        ? 'Оплата захищена Monobank Plata. Підтримуються будь-які українські та міжнародні картки Visa/MasterCard, Apple Pay, Google Pay.' 
                        : 'Secure checkout powered by Monobank Plata. Supports Visa, MasterCard, Apple Pay, Google Pay.'}
                    </div>
                  </div>
                )}

                {/* METHOD 2: DIRECT IBAN (FOP AKSJONOV) */}
                {paymentMethod === 'iban' && (
                  <div className="space-y-3 pt-2">
                    <div className="p-4 bg-violet-950/40 border border-violet-500/40 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-violet-300 font-mono flex items-center gap-1.5">
                          <Building className="w-4 h-4 text-violet-400" />
                          <span>{isUk ? 'Офіційні реквізити для оплати (ФОП Аксьонов С.М.):' : 'Official FOP Payment Requisites:'}</span>
                        </span>
                        <span className="text-xs font-bold text-emerald-400 font-mono">
                          {ibanDetails.amount} грн
                        </span>
                      </div>

                      {/* Fields grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono">
                        
                        {/* Recipient */}
                        <div className="p-2.5 bg-slate-900/90 rounded-lg border border-slate-800 flex items-center justify-between gap-2">
                          <div>
                            <span className="text-[10px] text-slate-400 block">{isUk ? 'Отримувач:' : 'Recipient:'}</span>
                            <span className="text-slate-200 font-semibold">{ibanDetails.recipient}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopyField(ibanDetails.recipient, 'recipient')}
                            className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"
                            title="Копіювати"
                          >
                            {copiedField === 'recipient' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>

                        {/* Tax ID */}
                        <div className="p-2.5 bg-slate-900/90 rounded-lg border border-slate-800 flex items-center justify-between gap-2">
                          <div>
                            <span className="text-[10px] text-slate-400 block">{isUk ? 'ЄДРПОУ / РНОКПП:' : 'Tax ID (EDRPOU):'}</span>
                            <span className="text-slate-200 font-bold">{ibanDetails.taxId}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopyField(ibanDetails.taxId, 'taxId')}
                            className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"
                            title="Копіювати"
                          >
                            {copiedField === 'taxId' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>

                        {/* IBAN */}
                        <div className="p-2.5 bg-slate-900/90 rounded-lg border border-slate-800 flex items-center justify-between gap-2 sm:col-span-2">
                          <div className="overflow-hidden">
                            <span className="text-[10px] text-slate-400 block">{isUk ? 'Рахунок у форматі IBAN (АТ «УНІВЕРСАЛ БАНК»):' : 'IBAN Account (Universal Bank):'}</span>
                            <span className="text-emerald-300 font-mono font-black text-sm tracking-wider select-all block truncate">{ibanDetails.iban}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopyField(ibanDetails.iban, 'iban')}
                            className="px-2.5 py-1.5 bg-violet-600/30 hover:bg-violet-600/50 text-violet-300 border border-violet-500/40 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer shrink-0"
                          >
                            {copiedField === 'iban' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                            <span>{copiedField === 'iban' ? 'Скопійовано' : 'Копіювати'}</span>
                          </button>
                        </div>

                        {/* Payment Purpose */}
                        <div className="p-2.5 bg-slate-900/90 rounded-lg border border-slate-800 flex items-center justify-between gap-2 sm:col-span-2">
                          <div className="overflow-hidden">
                            <span className="text-[10px] text-slate-400 block">{isUk ? 'Призначення платежу:' : 'Payment Purpose:'}</span>
                            <span className="text-slate-300 font-mono text-xs block truncate">{ibanDetails.purpose}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopyField(ibanDetails.purpose, 'purpose')}
                            className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors shrink-0"
                            title="Копіювати"
                          >
                            {copiedField === 'purpose' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>

                      </div>

                      {/* Actions */}
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-2 border-t border-slate-800/80">
                        <button
                          type="button"
                          onClick={() => handleCopyField(allIbanText, 'all')}
                          className="w-full sm:w-auto px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 cursor-pointer transition-colors"
                        >
                          {copiedField === 'all' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                          <span>{copiedField === 'all' ? 'Всі реквізити скопійовано' : 'Скопіювати всі реквізити'}</span>
                        </button>

                        <a
                          href={`mailto:fopaksjonov@gmail.com?subject=Оплата%20Deep%20Brain%20OS%20(${selectedPlan})&body=Вітаю!%20Здійснено%20оплату%20за%20ліцензію%20Deep%20Brain%20OS%20за%20реквізитами%20IBAN.%0AТариф:%20${getPlanTitle()}%0AСума:%20${ibanDetails.amount}%20грн%0AМій%20email:%20${email.trim()}%0AДодаю%20квитанцію.`}
                          target="_blank"
                          rel="noreferrer"
                          className="w-full sm:w-auto px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 cursor-pointer transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          <span>{isUk ? 'Надіслати квитанцію на fopaksjonov@gmail.com' : 'Send receipt to fopaksjonov@gmail.com'}</span>
                        </a>
                      </div>
                    </div>

                    <div className="text-[11px] text-slate-400 text-center font-mono">
                      {isUk 
                        ? 'Після здійснення банківського переказу ліцензійний ключ та архів будуть надіслані на вказаний email протягом 15 хвилин.' 
                        : 'After bank transfer, your license key and package will be delivered to your email within 15 minutes.'}
                    </div>
                  </div>
                )}

                {/* METHOD 3: INTERNATIONAL (LEMON SQUEEZY / SWIFT) */}
                {paymentMethod === 'international' && (
                  <div className="space-y-4 pt-2">
                    <div className="p-4 bg-blue-950/40 border border-blue-500/40 rounded-xl space-y-3 text-xs">
                      <div className="flex items-center gap-2 font-bold text-blue-300 font-mono">
                        <Globe className="w-4 h-4 text-blue-400" />
                        <span>{isUk ? 'Міжнародна оплата (USD / EUR / SWIFT / Lemon Squeezy)' : 'International Orders (USD / EUR)'}</span>
                      </div>
                      <p className="text-slate-300 leading-relaxed">
                        {isUk 
                          ? 'Для замовників з-за кордону або компаній, яким потрібен міжнародний інвойс із ПДВ (Lemon Squeezy Merchant of Record або прямий валютний SWIFT/SEPA контракт):' 
                          : 'For international clients needing VAT-compliant SaaS invoices via Lemon Squeezy or direct SWIFT/SEPA contracts:'}
                      </p>
                      <div className="p-3 bg-slate-900/90 rounded-lg border border-slate-800 space-y-1 font-mono text-[11px]">
                        <div><strong className="text-slate-200">Merchant of Record:</strong> Lemon Squeezy / FOP Aksjonov</div>
                        <div><strong className="text-slate-200">Contact Email:</strong> fopaksjonov@gmail.com</div>
                        <div><strong className="text-slate-200">Supported Currencies:</strong> USD ($), EUR (€), GBP (£)</div>
                      </div>
                      <a
                        href={`mailto:fopaksjonov@gmail.com?subject=International%20Order%20Deep%20Brain%20OS%20(${selectedPlan})&body=Hello,%20I%20would%20like%20to%20order%20Deep%20Brain%20OS%20(${getPlanTitle()}).%20Please%20issue%20an%20invoice%20for%20my%20email:%20${email.trim()}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-xs transition-colors cursor-pointer"
                      >
                        <Mail className="w-4 h-4" />
                        <span>{isUk ? 'Замовити міжнародний інвойс' : 'Request International Invoice'}</span>
                      </a>
                    </div>
                  </div>
                )}

              </div>

            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="p-4 sm:p-5 border-t border-slate-800 bg-slate-950/80 flex items-center justify-between gap-4 text-xs font-mono text-slate-400">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Deep Research Hub • https://deepresearchhub.online</span>
          </div>

          <div className="flex items-center gap-2">
            {activeTab !== 'pricing' && (
              <button
                onClick={() => setActiveTab('pricing')}
                className="px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
              >
                <span>{isUk ? 'Перейти до покупки ($79)' : 'Proceed to Purchase ($79)'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors cursor-pointer"
            >
              {isUk ? 'Закрити' : 'Close'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
export default DeepBrainOsShowcaseModal;
